<script lang="ts" module>
	/** The player's words, in the dashboard's language. */
	export interface PlayerLabels {
		play: string;
		pause: string;
		captions: string;
		fullscreen: string;
		exitFullscreen: string;
		seek: string;
	}
</script>

<script lang="ts">
	// The design system's VideoPlayer (design/components/surfaces/VideoPlayer.jsx,
	// D22 Help): plays one walkthrough, and owns three things a page must not
	// hand-roll.
	//
	// Captions are drawn by the player, never burnt into the recording, so they
	// stay readable at any size: over the frame at the bottom, or at the top
	// when the scene says its subject is at the bottom ("Tap Pay"), and under
	// the frame when the player is narrower than 560 px, where an overlay would
	// cover the scene or shrink past reading. Over the frame the video shows
	// through behind the words; under it the ground is solid. They start on.
	//
	// Time runs left to right in both languages, so the track and the clock sit
	// in dir="ltr", like the keypad. Scene starts are marked on the track.
	// Arrow keys step 5 s; Home and End jump. A tap on the track moves there.
	// The design's track also follows a dragging finger; the app allows no new
	// drag (.claude/rules/web.md), so here a tap moves and a drag does not.
	//
	// No download and no save. Native controls are never shown, because they
	// carry a download item; the context menu is stopped on the video, and
	// picture-in-picture and remote playback are off. Links are short-lived.
	//
	// A link that ran out is not a failure. When the video errors, the player
	// says nothing and tells the page, which may hand it a fresh link
	// (onLinkExpired); when the new `src` arrives it goes back to the same
	// second, and keeps playing or paused, as it was. While it loads, the time,
	// the scene and the caption stay where they were.
	//
	// Play works while the video loads, unlike the design's, because a browser
	// that loads nothing before a tap, as an iPhone may, loads on that tap.
	//
	// The design draws a stand-in frame when there is no recording yet, with
	// the title card and the press marker. Help always has the recording, which
	// carries its own, so this port plays the video alone.
	//
	// Full screen puts the whole player there, captions and all. An iPhone
	// cannot: it gives full screen to the video alone, in its own player,
	// which hides whatever is drawn over it. There the captions go in as the
	// video's own track (vttOf), shown only while that player is open.

	import type { Snippet } from 'svelte';
	import { untrack } from 'svelte';
	import Caption from './Caption.svelte';
	import Icon from './Icon.svelte';
	import { pressable } from './pressable';
	import { captionAt, clock, vttOf, type PlayerScene } from '../help/player';

	interface Props {
		testId: string;
		/** The video's link. */
		src: string;
		title: string;
		/** Seconds. */
		duration: number;
		scenes: PlayerScene[];
		dir: 'ltr' | 'rtl';
		lang: string;
		/** loading and error put `children` in the frame, so a failure is said in place. */
		state?: 'loading' | 'error' | 'ready';
		captions?: boolean;
		onCaptions?: (on: boolean) => void;
		/** Seconds into the video. Bind it to know which scene is playing. */
		time?: number;
		/** Plays from startAt each time startKey changes: a scene was picked. */
		startAt?: number;
		startKey?: number;
		/** The video can play. */
		onReady?: () => void;
		/**
		 * The video stopped loading or playing: its link may have run out.
		 * network says the network failed.
		 */
		onLinkExpired?: (seconds: number, network: boolean) => void;
		labels: PlayerLabels;
		children?: Snippet;
	}

	let {
		testId,
		src,
		title,
		duration,
		scenes,
		dir,
		lang,
		// Named phase here: a local called state shadows the $state rune.
		state: phase = 'ready',
		captions = true,
		onCaptions,
		time = $bindable(0),
		startAt = 0,
		startKey,
		onReady,
		onLinkExpired,
		labels,
		children,
	}: Props = $props();

	let root = $state<HTMLDivElement>();
	let video = $state<HTMLVideoElement>();
	let track = $state<HTMLTrackElement>();
	let playing = $state(false);
	let full = $state(false);
	let width = $state(800);

	const ready = $derived(phase === 'ready');
	const shown = $derived(ready && captions ? captionAt(scenes, time) : null);
	const narrow = $derived(width < 560);
	const percent = $derived(duration ? Math.min(100, (time / duration) * 100) : 0);

	// Where to go back to once a fresh link loads, and whether to play then.
	let resumeAt: number | null = null;
	let resumePlaying = false;

	// MediaError.MEDIA_ERR_NETWORK: the network failed. A browser reports an
	// HTTP error, an expired link's 403 as much as a 503, the same way as a
	// file it cannot play, so nothing else says the network was at fault.
	const NETWORK_ERROR = 2;

	function seek(seconds: number) {
		const to = Math.max(0, Math.min(duration, seconds));
		time = to;
		// A move while a fresh link loads is where the video goes on from.
		if (resumeAt !== null) resumeAt = to;
		if (video) video.currentTime = to;
	}

	function play() {
		void video?.play().catch(() => (playing = false));
	}

	function toggle() {
		if (phase === 'error' || !video) return;
		// A fresh link is loading: what the tap asks for happens once it has.
		// A browser that loads only on a tap has not started: this tap starts it.
		// A browser that loads only on a tap starts on this one.
		if (resumeAt !== null) {
			resumePlaying = !playing;
			if (playing) video.pause();
			else play();
			return;
		}
		if (!video.paused) {
			video.pause();
			return;
		}
		if (time >= duration) seek(0);
		play();
	}

	function failed() {
		// A place already waiting, such as a scene picked while loading, stays.
		if (resumeAt === null) {
			resumeAt = time;
			resumePlaying = playing;
		}
		// Nothing plays now, whatever comes next: a fresh link or a failure.
		playing = false;
		onLinkExpired?.(time, video?.error?.code === NETWORK_ERROR);
	}

	// The browser stopped loading before it read anything, as one that loads
	// only on a tap does: the video is there to play, at first or on a fresh
	// link, and nothing has failed. A fresh link goes on as the video was:
	// playing, if it played or a scene was picked. A browser that still wants
	// a tap of its own refuses, and the button stays on Play for that tap.
	function idle() {
		if (!video || video.readyState !== 0) return;
		if (resumePlaying) play();
		onReady?.();
	}

	function loaded() {
		// The player draws the captions itself, so the browser must not show
		// the track too, as Safari does when the system asks for captions.
		// While an iPhone's own player is open, the track is the only place
		// the words show (the videos have no sound), so it stays.
		const own = video as (HTMLVideoElement & { webkitDisplayingFullscreen?: boolean }) | undefined;
		if (track?.track && !own?.webkitDisplayingFullscreen) track.track.mode = 'disabled';
		if (video) {
			if (resumeAt !== null) video.currentTime = resumeAt;
			if (resumePlaying) play();
		}
		resumeAt = null;
		resumePlaying = false;
		onReady?.();
	}

	function fromClick(event: MouseEvent) {
		if (!ready) return;
		const bar = (event.currentTarget as HTMLElement).getBoundingClientRect();
		seek(((event.clientX - bar.left) / bar.width) * duration);
	}

	function step(event: KeyboardEvent) {
		if (!ready) return;
		const by: Partial<Record<string, number>> = {
			ArrowRight: 5,
			ArrowUp: 5,
			ArrowLeft: -5,
			ArrowDown: -5,
		};
		const move = by[event.key];
		if (move !== undefined) seek(time + move);
		else if (event.key === 'Home') seek(0);
		else if (event.key === 'End') seek(duration);
		else return;
		event.preventDefault();
	}

	function fullToggle() {
		if (document.fullscreenElement) {
			void document.exitFullscreen();
		} else if (root?.requestFullscreen) {
			void root.requestFullscreen().catch(videoAlone);
		} else {
			videoAlone();
		}
	}

	// An iPhone's own full screen player, with the captions as the video's track.
	function videoAlone() {
		const own = video as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | undefined;
		if (!own?.webkitEnterFullscreen) return;
		if (track?.track) track.track.mode = captions ? 'showing' : 'disabled';
		own.webkitEnterFullscreen();
	}

	// A scene was picked: play from its start, now or once a fresh link loads.
	$effect(() => {
		if (startKey === undefined) return;
		untrack(() => {
			if (ready && resumeAt === null) {
				seek(startAt);
				play();
			} else {
				resumeAt = startAt;
				resumePlaying = true;
				seek(startAt);
				// A browser that loads only on a tap loads on this one.
				if (video?.readyState === 0) play();
			}
		});
	});

	// A fresh link: go back to where the video was once it loads.
	let lastSrc = untrack(() => src);
	$effect.pre(() => {
		if (src === lastSrc) return;
		lastSrc = src;
		untrack(() => {
			if (resumeAt === null) {
				resumeAt = time;
				resumePlaying = playing;
			}
		});
	});

	// The captions as a track, for the iPhone's own player only: a blob of
	// the page's own origin, which a browser loads for a track with no CORS.
	// A data: address has no origin, and Safari's track loader, the one this
	// is for, may refuse it; Chrome takes both.
	const vttUrl = $derived(URL.createObjectURL(new Blob([vttOf(scenes)], { type: 'text/vtt' })));

	// Each address is let go once a newer one replaces it, or the player goes.
	$effect(() => {
		const url = vttUrl;
		return () => URL.revokeObjectURL(url);
	});

	$effect(() => {
		const own = video;
		if (!own) return;
		const leave = () => {
			if (track?.track) track.track.mode = 'disabled';
		};
		own.addEventListener('webkitendfullscreen', leave);
		return () => own.removeEventListener('webkitendfullscreen', leave);
	});

	$effect(() => {
		const changed = () => (full = !!root && document.fullscreenElement === root);
		document.addEventListener('fullscreenchange', changed);
		return () => document.removeEventListener('fullscreenchange', changed);
	});
</script>

<div
	bind:this={root}
	bind:clientWidth={width}
	class="player"
	class:full
	{dir}
	{lang}
	data-video-player
	data-testid={testId}
>
	<div class="frame">
		{#if src && phase !== 'error'}
			<video
				bind:this={video}
				{src}
				playsinline
				preload="metadata"
				controlslist="nodownload noremoteplayback noplaybackrate"
				disablepictureinpicture
				disableremoteplayback
				aria-label={title}
				data-testid="{testId}-video"
				onloadedmetadata={loaded}
				onerror={failed}
				onsuspend={idle}
				onloadstart={() => (playing = false)}
				ontimeupdate={() => {
					// A fresh link starts at 0 until it loads and goes back.
					if (video && resumeAt === null) time = video.currentTime;
				}}
				onplay={() => (playing = true)}
				onpause={() => (playing = false)}
				onended={() => (playing = false)}
				oncontextmenu={(event) => event.preventDefault()}
			>
				<track
					bind:this={track}
					kind="captions"
					src={vttUrl}
					srclang={lang}
					label={labels.captions}
				/>
			</video>
		{/if}
		{#if !ready}
			<div class="state" role={phase === 'error' ? 'alert' : 'status'}>
				{@render children?.()}
			</div>
		{/if}
		{#if shown && !narrow}
			<div class="over" class:top={shown.place === 'top'} aria-live="polite">
				<Caption text={shown.caption} size="lg" {dir} {lang} />
			</div>
		{/if}
	</div>
	{#if narrow && ready && captions}
		<div class="under" aria-live="polite">
			{#if shown}
				<Caption text={shown.caption} over={false} {dir} {lang} />
			{/if}
		</div>
	{/if}
	<div class="controls">
		<button
			type="button"
			class="control square"
			data-press
			data-testid="{testId}-play"
			disabled={phase === 'error'}
			aria-label={playing ? labels.pause : labels.play}
			onclick={toggle}
			{@attach pressable}
		>
			<Icon name={playing ? 'pause' : 'play'} size={22} />
		</button>
		<span class="clock" dir="ltr">{clock(time)} / {clock(duration)}</span>
		<div
			class="track"
			class:off={!ready}
			dir="ltr"
			role="slider"
			tabindex={ready ? 0 : -1}
			aria-label={labels.seek}
			aria-valuemin={0}
			aria-valuemax={Math.round(duration)}
			aria-valuenow={Math.round(time)}
			aria-valuetext="{clock(time)} / {clock(duration)}"
			aria-disabled={ready ? undefined : 'true'}
			data-press="bare"
			data-testid="{testId}-seek"
			onclick={fromClick}
			onkeydown={step}
			{@attach pressable}
		>
			<span class="rail">
				<span class="done" style:inline-size="{percent}%"></span>
				{#each scenes as s, i (i)}
					{#if i > 0 && duration}
						<span
							class="mark"
							aria-hidden="true"
							style:inset-inline-start="{(s.at / duration) * 100}%"
						></span>
					{/if}
				{/each}
				<span class="thumb" aria-hidden="true" style:inset-inline-start="{percent}%"></span>
			</span>
		</div>
		<button
			type="button"
			class="control"
			class:on={captions}
			data-press
			data-testid="{testId}-captions"
			aria-pressed={captions}
			onclick={() => onCaptions?.(!captions)}
			{@attach pressable}
		>
			{labels.captions}
		</button>
		<button
			type="button"
			class="control square"
			data-press
			data-testid="{testId}-fullscreen"
			aria-label={full ? labels.exitFullscreen : labels.fullscreen}
			onclick={fullToggle}
			{@attach pressable}
		>
			<Icon name="fullscreen" size={22} />
		</button>
	</div>
</div>

<style>
	.player {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
		min-inline-size: 0;
	}

	.player.full {
		justify-content: center;
		padding: var(--space-5);
		background: var(--surface-page);
	}

	.frame {
		position: relative;
		inline-size: 100%;
		margin-inline: auto;
		aspect-ratio: 16 / 9;
		overflow: hidden;
		background: var(--surface-well);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-card);
	}

	.full .frame {
		inline-size: min(100%, calc((100vh - 140px) * 16 / 9));
	}

	video {
		display: block;
		inline-size: 100%;
		block-size: 100%;
		object-fit: contain;
		background: var(--surface-page);
	}

	.state {
		position: absolute;
		inset: 0;
		display: grid;
		place-items: center;
		padding: var(--space-7);
		text-align: center;
		background: var(--surface-well);
	}

	.over {
		position: absolute;
		inset-inline: 4%;
		inset-block-end: 5%;
		display: flex;
		justify-content: center;
		pointer-events: none;
	}

	.over.top {
		inset-block-start: 5%;
		inset-block-end: auto;
	}

	.under {
		display: flex;
		justify-content: center;
		min-block-size: calc(var(--text-base) * 2.9 + var(--space-4));
	}

	.controls {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-3) var(--space-4);
	}

	/* The design's Button, secondary at size sm, copied as the design does. */
	.control {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		min-block-size: var(--ctl-min);
		min-inline-size: var(--ctl-min);
		padding: 0 12px;
		font-family: var(--font-sans);
		font-size: var(--text-sm);
		font-weight: 600;
		white-space: nowrap;
		background: var(--surface-card);
		color: var(--text-strong);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-btn);
		cursor: pointer;
	}

	.control.square {
		padding: 0;
	}

	.control.on {
		background: var(--surface-invert);
		color: var(--on-ink);
		border-color: var(--surface-invert);
	}

	.clock {
		font-family: var(--font-mono);
		font-size: var(--text-sm);
		font-weight: 600;
		color: var(--text-strong);
		white-space: nowrap;
	}

	.track {
		flex: 1 1 160px;
		min-inline-size: 0;
		position: relative;
		block-size: var(--ctl-min);
		display: flex;
		align-items: center;
		cursor: pointer;
		border-radius: var(--radius-input);
	}

	.track.off {
		cursor: default;
	}

	.rail {
		position: relative;
		flex: 1;
		block-size: 6px;
		border-radius: var(--radius-pill);
		background: var(--surface-track);
	}

	.done {
		position: absolute;
		inset-block: 0;
		inset-inline-start: 0;
		border-radius: inherit;
		background: var(--text-strong);
	}

	.mark {
		position: absolute;
		inset-block: 0;
		inline-size: 2px;
		background: var(--surface-page);
	}

	.thumb {
		position: absolute;
		inset-block-start: 50%;
		inline-size: 16px;
		block-size: 16px;
		transform: translate(-50%, -50%);
		border-radius: 50%;
		background: var(--text-strong);
		border: 2px solid var(--surface-page);
	}
</style>
